import { ReactNode, useMemo } from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { Auth0Provider } from "@auth0/auth0-react";
import App from "./App";
import "./index.css";
import { AuthBridgeProvider, MockAuthProvider } from "./hooks/useAuth";
import { env } from "./lib/env";
import { initAnalytics } from "./lib/telemetry";
import { initWebVitals } from "./lib/webVitals";
import { initSentry } from "./lib/sentry";

const domain = env.auth0Domain;
const clientId = env.auth0ClientId;
const audience = env.auth0Audience || undefined;

function AuthProvider({ children }: { children: ReactNode }) {
  const isConfigured = Boolean(domain && clientId);
  const useMockAuth = import.meta.env.VITE_E2E_MOCK_AUTH === 'true';
  const redirectUri = useMemo(() => window.location.origin, []);

  if (useMockAuth) {
    if (!isConfigured) {
      console.warn("Auth0 environment variables are missing. Using mock auth provider.");
    }
    return <MockAuthProvider>{children}</MockAuthProvider>;
  }

  if (!isConfigured) {
    console.error("Auth0 environment variables are missing; set VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID.");
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 text-center text-sm text-slate-200">
        <div className="max-w-md space-y-3 rounded-xl border border-white/10 bg-white/[0.04] p-6">
          <p className="text-lg font-semibold text-white">Authentication not configured</p>
          <p className="text-slate-300">
            Auth0 environment variables are missing. Set <code className="font-mono">VITE_AUTH0_DOMAIN</code> and{' '}
            <code className="font-mono">VITE_AUTH0_CLIENT_ID</code> for the jobs app, then redeploy.
          </p>
        </div>
      </div>
    );
  }

  return (
    <Auth0Provider
      domain={domain!}
      clientId={clientId!}
      authorizationParams={{
        redirect_uri: redirectUri,
        audience,
        scope: "openid profile email offline_access",
      }}
      cacheLocation="localstorage"
      useRefreshTokens
      onRedirectCallback={(appState) => {
        const target = appState?.returnTo || window.location.pathname;
        window.history.replaceState({}, document.title, target);
        window.dispatchEvent(new PopStateEvent("popstate"));
      }}
    >
      <AuthBridgeProvider>{children}</AuthBridgeProvider>
    </Auth0Provider>
  );
}

initAnalytics();
initWebVitals();
initSentry();

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <AuthProvider>
      <App />
    </AuthProvider>
  </HelmetProvider>
);
